import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { useScroll, motion } from "framer-motion";
import "./App.css";
import Header from "./components/Header";
import Footer, { ContactBar } from "./components/Footer";
import { QuoteProvider } from "./components/QuotePanel";
import { MetaTitle } from "./meta";
import Home from "./pages/Home";
import Servizi from "./pages/Servizi";
import ServizioDettaglio from "./pages/ServizioDettaglio";
import CentraleOperativa from "./pages/CentraleOperativa";
import ObiettiviSensibili from "./pages/ObiettiviSensibili";
import ChiSiamo from "./pages/ChiSiamo";
import Contatti from "./pages/Contatti";
import Preventivo from "./pages/Preventivo";
import Privacy from "./pages/Privacy";
import Cookie from "./pages/Cookie";
import Accessibilita from "./pages/Accessibilita";
import Whistleblowing from "./pages/Whistleblowing";
import CookieBanner from "./components/CookieBanner";
import { fixWidgetA11y } from "./aichat";

/* Chat Keplero: montata dinamicamente su ogni pagina TRANNE /whistleblowing
   (pagina senza tracciamento: ne' chat, ne' strumenti di misura). */
function ChatGate() {
  const { pathname } = useLocation();
  const skip = pathname === "/whistleblowing";
  useEffect(() => {
    if (skip) {
      const el = document.querySelector("kepleroai-chat");
      if (el) el.remove();
      return undefined;
    }
    if (!document.querySelector("script[src*='bundle.keplero.ai']")) {
      const s = document.createElement("script");
      s.src = "https://bundle.keplero.ai/min.js";
      s.defer = true;
      document.body.appendChild(s);
    }
    if (!document.querySelector("kepleroai-chat")) {
      const el = document.createElement("kepleroai-chat");
      el.setAttribute("chatbot", "824df35b-9311-4861-9fb1-c40dd502d001");
      document.body.appendChild(el);
    }
    return undefined;
  }, [skip]);
  return null;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 60, fontFamily: "sans-serif" }}>
          <h1>Errore inatteso</h1>
          <p>Ricarica la pagina; se il problema persiste segnala cosa stavi visualizzando.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

/* Al cambio di percorso: scroll immediato a 0.
   Con i tasti Avanti/Indietro del browser (POP) la posizione viene ripristinata dal browser. */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === "POP") return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, navType]);
  return null;
}

/* Classe .scrolling sul body durante lo scorrimento: disattiva hover e
   pointer-events su schede, foto, loghi e badge (rimossa 150ms dopo l'ultimo evento). */
function ScrollingClass() {
  useEffect(() => {
    let t;
    const on = () => {
      document.body.classList.add("scrolling");
      clearTimeout(t);
      t = setTimeout(() => document.body.classList.remove("scrolling"), 150);
    };
    window.addEventListener("scroll", on, { passive: true });
    return () => {
      window.removeEventListener("scroll", on);
      clearTimeout(t);
      document.body.classList.remove("scrolling");
    };
  }, []);
  return null;
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return <motion.div className="sprog" style={{ scaleX: scrollYProgress }} aria-hidden="true" />;
}

function Shell() {
  useEffect(() => fixWidgetA11y(), []);
  return (
    <>
      <ScrollToTop />
      <ScrollingClass />
      <ScrollProgress />
      <MetaTitle />
      <ChatGate />
      <a href="#main" className="skip-link" data-testid="skip-link">
        Vai al contenuto
      </a>
      <Header />
      <main id="main" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/servizi" element={<Servizi />} />
          <Route path="/servizi/:servizioId" element={<ServizioDettaglio />} />
          <Route path="/preventivo" element={<Preventivo />} />
          <Route path="/centrale-operativa" element={<CentraleOperativa />} />
          <Route path="/obiettivi-sensibili" element={<ObiettiviSensibili />} />
          <Route path="/chi-siamo" element={<ChiSiamo />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookie" element={<Cookie />} />
          <Route path="/accessibilita" element={<Accessibilita />} />
          <Route path="/whistleblowing" element={<Whistleblowing />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
      <ContactBar />
      <CookieBanner />
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QuoteProvider>
          <Shell />
        </QuoteProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
